import React, { useState, useEffect, useCallback, useMemo } from "react";
import { dsrService } from "../services/dsrService";
import { toast } from "react-toastify";
import Pagination from "../common/pagination";
import DatePicker from "../../components/modal/datepicker";
import Dropdown from "../../components/modal/dropdown";
import Can from "../common/can";
import { FaEye, FaEdit, FaTrash } from "react-icons/fa";
import DSRModal from "./dsr_modal";
import ViewDSR from "./view_dsr";
import { DSRStatusFilterOptions, getDSRStatusLabel, getDSRStatusClass } from "../../constants/constants";
import "../attendance/attendance.css";

const PAGE_SIZE = 20;

const WEEK_FETCH_SIZE = 500;

const getCurrentWeekRange = () => {
  const now = new Date();
  const day = now.getDay(); 
  const diffToMonday = day === 0 ? -6 : 1 - day;

  const monday = new Date(now);
  monday.setDate(now.getDate() + diffToMonday);
  monday.setHours(0, 0, 0, 0);

  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);

  const toISODate = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
  };

  return { start: toISODate(monday), end: toISODate(sunday) };
};

const formatWeekLabel = (start, end) => {
  const opts = { month: "short", day: "numeric" };
  const startLabel = new Date(`${start}T00:00:00`).toLocaleDateString(undefined, opts);
  const endLabel = new Date(`${end}T00:00:00`).toLocaleDateString(undefined, { ...opts, year: "numeric" });
  return `${startLabel} – ${endLabel}`;
};

const DSRManagement = () => {
  const [dsrs, setDsrs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  // Only meaningful in single-date mode (server-paginated). In week mode
  // pagination is computed client-side from the filtered row count.
  const [serverTotalCount, setServerTotalCount] = useState(0);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  // Empty = "show the current week" (default). Once set, this narrows
  // the view down to just that single day, same as the original
  // behavior, and the week range below is no longer used for fetching.
  const [dateFilter, setDateFilter] = useState("");

  const [viewDSR, setViewDSR] = useState(null);
  const [editDSR, setEditDSR] = useState(null);
  const [deleteDSR, setDeleteDSR] = useState(null);

  // Fixed to the current week — recomputed only if the component stays
  // mounted across a real day change, which for a dashboard-style page is
  // effectively "computed once per load".
  const weekRange = useMemo(() => getCurrentWeekRange(), []);
  const isDateMode = Boolean(dateFilter);

  const confirmDelete = async () => {
    if (!deleteDSR) return;
    try {
      await dsrService.deleteDSR(deleteDSR.id);
      toast.success("DSR deleted successfully.");
      setDeleteDSR(null);
      fetchDSRs();
    } catch (err) {
      toast.error(err.response?.data?.message || err.response?.data?.detail || "Failed to delete DSR.");
    }
  };

  const fetchDSRs = useCallback(async () => {
    try {
      setLoading(true);

      if (isDateMode) {
        // Single-date mode: same as the original behavior — the backend
        // already filters correctly by an exact "date", so use real
        // server-side pagination here.
        const params = { page, page_size: PAGE_SIZE, date: dateFilter };
        if (statusFilter !== "all") params.status = statusFilter;

        const res = await dsrService.getDSRs(params);
        const data = res.data || res;
        const meta = res.meta || {};
        setDsrs(Array.isArray(data) ? data : data.results || []);
        setServerTotalCount(meta.count ?? data.count ?? (Array.isArray(data) ? data.length : 0));
      } else {
        // Week mode (default): no server-side date-range support, so pull
        // one large batch for the status filter and narrow to the
        // current week + paginate entirely on the client (see the
        // displayedDsrs / pagedDsrs memos below).
        const params = { page: 1, page_size: WEEK_FETCH_SIZE };
        if (statusFilter !== "all") params.status = statusFilter;
        // Best-effort — harmless if the backend ignores these.
        params.start_date = weekRange.start;
        params.end_date = weekRange.end;

        const res = await dsrService.getDSRs(params);
        const data = res.data || res;
        setDsrs(Array.isArray(data) ? data : data.results || []);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || err.response?.data?.detail || "Failed to load DSRs");
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, dateFilter, isDateMode, weekRange]);

  useEffect(() => {
    fetchDSRs();
  }, [fetchDSRs]);

  useEffect(() => { setPage(1); }, [search, statusFilter, dateFilter]);

  /* ── CLIENT-SIDE FILTERING ──
     Week mode: restrict the fetched batch to the current Monday–Sunday
     range (the source of truth for "this week", since the backend can't
     be relied on to do that filtering itself), then apply search.
     Date mode: the backend already returned just that date, so this only
     applies the search — same as the original code's behavior. */
  const displayedDsrs = useMemo(() => {
    let rows = dsrs;
    if (!isDateMode) {
      rows = rows.filter((d) => d.date >= weekRange.start && d.date <= weekRange.end);
    }

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter(
        (d) =>
          d.report_text?.toLowerCase().includes(q) ||
          d.employee_name?.toLowerCase().includes(q) ||
          d.emp_id?.toLowerCase().includes(q)
      );
    }

    return rows;
  }, [dsrs, search, isDateMode, weekRange]);

  // Week mode paginates client-side over the already-filtered rows.
  // Date mode keeps the original server-side pagination as-is.
  const totalCount = isDateMode ? serverTotalCount : displayedDsrs.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const pagedDsrs = useMemo(() => {
    if (isDateMode) return displayedDsrs;
    const startIdx = (page - 1) * PAGE_SIZE;
    return displayedDsrs.slice(startIdx, startIdx + PAGE_SIZE);
  }, [displayedDsrs, isDateMode, page]);

  return (
    <div className="container">
      <div className="page-header">
        <h2>Daily Status Reports</h2>
      </div>

      <div className="filters-card single" style={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
        <input
          className="search-input"
          style={{ flex: "2 1 240px" }}
          placeholder="🔍 Search by Report text, Name, or Emp ID"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div style={{ flex: "1 1 180px" }}>
          <Dropdown
            options={DSRStatusFilterOptions}
            value={statusFilter}
            onChange={setStatusFilter}
            placeholder="Status"
          />
        </div>
        <div style={{ flex: "1 1 200px" }}>
          <DatePicker
            value={dateFilter}
            onChange={setDateFilter}
            placeholder="Select date"
          />
        </div>
        {isDateMode ? (
          <button
            type="button"
            className="primary-btn"
            style={{ flex: "0 0 auto" }}
            onClick={() => setDateFilter("")}
          >
            Back to this week
          </button>
        ) : (
          <span className="hr-live-pill" style={{ flex: "0 0 auto" }}>
            This Week: {formatWeekLabel(weekRange.start, weekRange.end)}
          </span>
        )}
      </div>

      {loading ? (
        <div className="empty-state"><p>Loading DSRs…</p></div>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Emp ID</th>
              <th>Employee Name</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pagedDsrs.length === 0 ? (
              <tr>
                <td colSpan="5">
                  <div className="empty-state">
                     <img src="/images/dsr.svg" alt="No Dsr" className="empty-img" />
                    <p>{isDateMode ? "No Daily Status Reports found for this date" : "No Daily Status Reports found for this week"}</p>
                  </div>
                </td>
              </tr>
            ) : (
              pagedDsrs.map((d) => (
                <tr key={d.id}>
                  <td>{d.date}</td>
                  <td>{d.emp_id}</td>
                  <td>{d.employee_name}</td>
                  <td>
                    <span className={`emp-status ${getDSRStatusClass(d.status)}`}>
                      {getDSRStatusLabel(d.status)}
                    </span>
                  </td>
                  <td className="action-icons">
                    <button className="icon-btn view" title="View DSR" onClick={() => setViewDSR(d)}>
                      <FaEye />
                    </button>
                    <Can perm="dsr.update">
                      <button className="icon-btn edit" title="Edit DSR" onClick={() => setEditDSR(d)}>
                        <FaEdit />
                      </button>
                    </Can>
                    <Can perm="dsr.delete">
                      <button className="icon-btn delete" title="Delete DSR" onClick={() => setDeleteDSR(d)}>
                        <FaTrash />
                      </button>
                    </Can>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}

      <Pagination page={page} totalPages={totalPages} setPage={setPage} />

      {viewDSR && (
        <ViewDSR
          dsr={viewDSR}
          onClose={() => setViewDSR(null)}
        />
      )}
      {editDSR && (
        <DSRModal
          existingDSR={editDSR}
          onClose={() => setEditDSR(null)}
          onSuccess={() => {
            setEditDSR(null);
            fetchDSRs();
          }}
        />
      )}

      {deleteDSR && (
        <div className="popup-overlay" onClick={() => setDeleteDSR(null)}>
          <div className="popup-content premium-profile" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "400px" }}>
            <div className="page-header" style={{ marginBottom: 6 }}>
              <h2 style={{ fontSize: 18, color: "#ef4444" }}>Confirm Delete</h2>
              <button className="profile-close" onClick={() => setDeleteDSR(null)}>&times;</button>
            </div>
            <hr className="premium-divider" />
            <div style={{ marginTop: "16px", fontSize: "15px", color: "#4b5563" }}>
              Are you sure you want to delete the Daily Status Report for <strong>{deleteDSR.employee_name}</strong> on <strong>{deleteDSR.date}</strong>? This action cannot be undone.
            </div>
            <div className="employee-actions" style={{ display: "flex", justifyContent: "flex-end", gap: "12px", marginTop: 24 }}>
              <button className="secondary" onClick={() => setDeleteDSR(null)}>Cancel</button>
              <button className="primary" style={{ background: "#ef4444", borderColor: "#ef4444" }} onClick={confirmDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DSRManagement;